import { baseApi } from '@krp/client-runtime'

import { PLATFORM_CATALOG_TAG } from '../../catalog-links/api/catalogLinksApi'
import type { CreateCommonProductRequest, PatchCommonProductRequest } from '../model/requests'
import type { CommonProductEditResult, CommonProductEdited } from '../model/types'
import { decodeEditResult, decodeEditedProduct } from './contracts'
import { PLATFORM_COMMON_PRODUCT_URLS } from './urls'

/**
 * Правка общего товара в общем `baseApi`. Команды несут `operationId` намерения человека и не повторяются transport-ом
 * (ADR-11): повтор тем же ключом сервер узнаёт сам. Товар читается тегом самого товара; любая команда перечитывает и
 * общий каталог, и связи — они показывают те же поля.
 */
export interface CommonProductArgs {
  productId: string
}

export const commonProductApi = baseApi.enhanceEndpoints({ addTagTypes: [PLATFORM_CATALOG_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    platformCommonProduct: build.query<CommonProductEdited, CommonProductArgs>({
      query: ({ productId }) => ({
        url: PLATFORM_COMMON_PRODUCT_URLS.one(productId),
        decode: (raw: unknown) => decodeEditedProduct(raw),
      }),
      providesTags: (_result, _error, { productId }) => [{ type: PLATFORM_CATALOG_TAG, id: `PRODUCT:${productId}` }],
    }),
    platformCommonProductCreate: build.mutation<CommonProductEditResult, { body: CreateCommonProductRequest }>({
      query: ({ body }) => ({ url: PLATFORM_COMMON_PRODUCT_URLS.products, method: 'POST', body, decode: decodeEditResult }),
      invalidatesTags: [PLATFORM_CATALOG_TAG],
    }),
    platformCommonProductPatch: build.mutation<CommonProductEditResult, { productId: string; body: PatchCommonProductRequest }>({
      query: ({ productId, body }) => ({
        url: PLATFORM_COMMON_PRODUCT_URLS.one(productId),
        method: 'PATCH',
        body,
        decode: decodeEditResult,
      }),
      invalidatesTags: [PLATFORM_CATALOG_TAG],
    }),
    platformCommonProductArchive: build.mutation<
      CommonProductEditResult,
      { productId: string; operationId: string; expectedVersion: number }
    >({
      query: ({ productId, operationId, expectedVersion }) => ({
        url: PLATFORM_COMMON_PRODUCT_URLS.one(productId),
        method: 'DELETE',
        params: { operationId, expectedVersion },
        decode: decodeEditResult,
      }),
      invalidatesTags: [PLATFORM_CATALOG_TAG],
    }),
  }),
})

export const {
  platformCommonProduct,
  platformCommonProductCreate,
  platformCommonProductPatch,
  platformCommonProductArchive,
} = commonProductApi.endpoints
